/**
 * Preloaded standard contract templates for deploy + mint flows.
 *
 * Each template points at an embedded artifact (ABI + bytecode + mint function).
 * Today multiple logical templates share the same audited test ERC-721 artifact;
 * add new keys under ARTIFACTS when you ship distinct bytecode per vertical.
 */
import { RWA_SHARED_SERIES_ERC721 } from './rwaSharedSeriesErc721Artifact';
import { RWA_SHARED_ERC20 } from './rwaSharedErc20Artifact';
import { RWA_SHARED_ERC1155 } from './rwaSharedErc1155Artifact';

const ARTIFACTS = {
  /**
   * Shared catalog CBC721 (CIP-721): approvals, Approval/ApprovalForAll, supportsInterface, safeTransfer with
   * onCBC721Received then onERC721Received; owner + minters; constructor (name, symbol, address[] initialMinters).
   * Source: contracts/catalog/RwaSharedSeriesErc721.sol — regenerate via scripts/embed-platform-catalog-artifacts.mjs.
   */
  offlineMintErc721SeriesV1: RWA_SHARED_SERIES_ERC721,
  /** contracts/catalog/RwaSharedErc20.sol */
  platformRwaErc20V1: RWA_SHARED_ERC20,
  /** contracts/catalog/RwaSharedErc1155.sol */
  platformRwaErc1155V1: RWA_SHARED_ERC1155
};

/** Internal template id for deploy UI when CBC20 grid card is selected (not listed in template picker). */
export const PLATFORM_PREBUILT_ERC20_TEMPLATE_ID = 'platform_rwa_erc20_v1';

/** Internal template id when CBC1155 grid card is selected. */
export const PLATFORM_PREBUILT_ERC1155_TEMPLATE_ID = 'platform_rwa_erc1155_v1';

export const DEFAULT_STANDARD_CONTRACT_TEMPLATE_ID = 'generic_rwa_erc721_v1';

/** Optional path: tokenizer supplies ABI/bytecode for their own Ping deployment. */
export const CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID = 'custom_tokenizer_ping';

const TEMPLATES = [
  {
    id: 'real_estate_series_erc721_v1',
    name: 'Real estate — standardized series',
    summary: 'ERC-721 collection for titled or series units (apartments, pads, lots).',
    chainStandard: 'ERC-721',
    versionLabel: 'v1 (series mint)',
    artifactKey: 'offlineMintErc721SeriesV1',
    suggestedCategories: ['realEstate', 'realestate', 'real_estate']
  },
  {
    id: 'commodity_bar_erc721_v1',
    name: 'Commodities — bars & lots',
    summary: 'ERC-721 for individually identified bars, bags, or graded lots (gold, silver, coffee, etc.).',
    chainStandard: 'ERC-721',
    versionLabel: 'v1 (series mint)',
    artifactKey: 'offlineMintErc721SeriesV1',
    suggestedCategories: ['preciousMetals', 'preciousmetals', 'agriculture', 'commodity']
  },
  {
    id: 'energy_asset_erc721_v1',
    name: 'Energy & infrastructure',
    summary: 'ERC-721 for projects, tranches, or facility-backed instruments (solar, wind, grid).',
    chainStandard: 'ERC-721',
    versionLabel: 'v1 (series mint)',
    artifactKey: 'offlineMintErc721SeriesV1',
    suggestedCategories: ['energy', 'infrastructure', 'utilities']
  },
  {
    id: 'generic_rwa_erc721_v1',
    name: 'Generic RWA — ERC-721 series',
    summary: 'Default template when no vertical-specific package is selected.',
    chainStandard: 'ERC-721',
    versionLabel: 'v1 (series mint)',
    artifactKey: 'offlineMintErc721SeriesV1',
    suggestedCategories: []
  }
];

/**
 * @returns {ReadonlyArray<{ id: string, name: string, summary: string, chainStandard: string, versionLabel: string }>}
 */
export function listStandardContractTemplates() {
  const preloaded = TEMPLATES.map(({ id, name, summary, chainStandard, versionLabel }) => ({
    id,
    name,
    summary,
    chainStandard,
    versionLabel
  }));
  return [
    ...preloaded,
    {
      id: CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID,
      name: 'Custom — my Ping contract',
      summary:
        'Optional: paste your own contract ABI (and creation bytecode when deploying a new collection). Use when your organization already has or deploys its own contracts on Ping.',
      chainStandard: 'Custom',
      versionLabel: 'tokenizer-provided'
    }
  ];
}

export function isCustomTokenizerContractTemplate(templateId) {
  return templateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID;
}

/**
 * Declared EVM standard for sync on deploy (see DeployContractRequest.declaredTokenStandard).
 * @param {string | undefined} contractTemplateId
 * @returns {'ERC721'|'ERC20'|'ERC1155'|undefined}
 */
export function getDeclaredTokenStandardForContractTemplate(contractTemplateId) {
  if (!contractTemplateId || contractTemplateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID) return undefined;
  if (contractTemplateId === PLATFORM_PREBUILT_ERC20_TEMPLATE_ID) return 'ERC20';
  if (contractTemplateId === PLATFORM_PREBUILT_ERC1155_TEMPLATE_ID) return 'ERC1155';
  return 'ERC721';
}

export function getStandardContractTemplate(templateId) {
  if (templateId === PLATFORM_PREBUILT_ERC20_TEMPLATE_ID) {
    return {
      id: PLATFORM_PREBUILT_ERC20_TEMPLATE_ID,
      name: 'Platform CBC20 catalog',
      summary: 'Preloaded fungible catalog: owner + minters, mint and burn.',
      chainStandard: 'ERC-20',
      versionLabel: 'v1 (catalog)',
      artifactKey: 'platformRwaErc20V1'
    };
  }
  if (templateId === PLATFORM_PREBUILT_ERC1155_TEMPLATE_ID) {
    return {
      id: PLATFORM_PREBUILT_ERC1155_TEMPLATE_ID,
      name: 'Platform CBC1155 catalog',
      summary: 'Preloaded multi-token catalog: owner + minters, mint and burn.',
      chainStandard: 'ERC-1155',
      versionLabel: 'v1 (catalog)',
      artifactKey: 'platformRwaErc1155V1'
    };
  }
  if (templateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID) {
    return {
      id: CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID,
      name: 'Custom — my Ping contract',
      summary: 'Tokenizer-provided artifact',
      chainStandard: 'Custom',
      versionLabel: 'tokenizer-provided',
      artifactKey: null
    };
  }
  const found = TEMPLATES.find((t) => t.id === templateId);
  return found || TEMPLATES.find((t) => t.id === DEFAULT_STANDARD_CONTRACT_TEMPLATE_ID);
}

/**
 * Resolved artifact for API calls (deploy / prepare mint).
 * @returns {{ abiJson: string, bytecode: string, mintFunctionName: string, abi: object }}
 */
export function getArtifactForTemplate(templateId) {
  if (templateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID) {
    throw new Error('Use resolveContractArtifact() with custom ABI/bytecode for the custom Ping contract option.');
  }
  const t = getStandardContractTemplate(templateId);
  const artifact = ARTIFACTS[t.artifactKey];
  if (!artifact) {
    throw new Error(`No artifact registered for template "${templateId}" (${t.artifactKey}).`);
  }
  return artifact;
}

/**
 * Resolve deploy/mint artifact: preloaded template or tokenizer-provided custom contract.
 * @param {string} templateId
 * @param {{ abiJson?: string, bytecode?: string, mintFunctionName?: string }} custom — required when template is custom
 */
export function resolveContractArtifact(templateId, custom = {}) {
  if (templateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID) {
    const abiJson = String(custom.abiJson ?? '').trim();
    if (!abiJson) {
      throw new Error('Custom contract: paste the contract ABI as JSON (array).');
    }
    let parsed;
    try {
      parsed = JSON.parse(abiJson);
    } catch {
      throw new Error('Custom contract: ABI must be valid JSON.');
    }
    if (!Array.isArray(parsed)) {
      throw new Error('Custom contract: ABI must be a JSON array (standard Solidity ABI).');
    }
    const normalized = JSON.stringify(parsed);
    const mintFunctionName = String(custom.mintFunctionName ?? 'mint').trim() || 'mint';
    const bytecode = String(custom.bytecode ?? '').trim();
    return {
      abiJson: normalized,
      bytecode,
      mintFunctionName,
      abi: parsed
    };
  }
  return getArtifactForTemplate(templateId);
}

/**
 * Pick a sensible default from asset class category (frontend `category` field).
 */
/**
 * Category-based default only; never returns custom — tokenizers opt in explicitly.
 */
export function suggestStandardContractTemplateId(assetCategory) {
  const c = String(assetCategory || '')
    .toLowerCase()
    .replace(/[\s_-]/g, '');

  for (const t of TEMPLATES) {
    for (const sc of t.suggestedCategories) {
      const n = String(sc).toLowerCase().replace(/[\s_-]/g, '');
      if (n && c.includes(n)) {
        return t.id;
      }
    }
  }

  return DEFAULT_STANDARD_CONTRACT_TEMPLATE_ID;
}
